import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../services/apiService'

interface TeamEnrichedInitiative {
  key: string
  summary: string
  team: string
  teamMapping: {
    confidence: number
    matchType: string
    originalSprint: string
  }
  customFields: any
  status: any
  priority: any
  assignee: any
  created: string
  updated: string
  // Story points fields from team enrichment
  totalStoryPoints?: number
  completedStoryPoints?: number
  // Team enrichment fields
  teams?: string[]
  teamSource?: string
  childEpicTeams?: string[]
  epics?: any[]
  [key: string]: any
}

interface UseTeamEnrichedDataOptions {
  selectedTeam?: string
  enabled?: boolean
}

export const useTeamEnrichedData = (options: UseTeamEnrichedDataOptions = {}) => {
  const { selectedTeam, enabled = true } = options

  return useQuery({
    queryKey: ['team-enriched-initiatives', selectedTeam],
    queryFn: async (): Promise<TeamEnrichedInitiative[]> => {
      console.log('🔍 useTeamEnrichedData: Starting API call for team:', selectedTeam)

      const url = selectedTeam
        ? `/team-filters/initiatives?team=${encodeURIComponent(selectedTeam)}`
        : '/team-filters/initiatives'

      console.log('🔍 useTeamEnrichedData: Making request to:', url)

      try {
        const response = await apiClient.get(url)
        const data = response.data

        console.log('✅ useTeamEnrichedData: API call successful for team:', selectedTeam, 'Count:', data.data?.length)

        if (!data.success) {
          throw new Error(data.message || 'Failed to load team-enriched data')
        }

        return data.data || []
      } catch (error) {
        console.error('❌ useTeamEnrichedData: API call failed for team:', selectedTeam, 'Error:', error)
        throw error
      }
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 2, // Add retry logic
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000) // Exponential backoff
  })
}

export const useTeams = () => {
  return useQuery({
    queryKey: ['teams', 'v3'], // Version bump to force refresh
    queryFn: async (): Promise<string[]> => {
      const response = await apiClient.get('/team-filters/teams')
      const data = response.data

      if (!data.success) {
        throw new Error(data.message || 'Failed to load teams')
      }

      return data.data || []
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false
  })
}

/**
 * Hook to get teams from the new team configuration API
 * This provides teams from the authoritative team configuration
 */
export const useTeamsFromConfig = () => {
  return useQuery({
    queryKey: ['teams-config', 'v1'],
    queryFn: async (): Promise<string[]> => {
      // Import here to avoid circular dependencies
      const { teamConfigService } = await import('../services/teamConfigService')
      const response = await teamConfigService.getTeamNames()

      if (!response.success) {
        throw new Error(response.message || 'Failed to load teams from configuration')
      }

      return response.data || []
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  })
}

export const useTeamStats = () => {
  return useQuery({
    queryKey: ['team-stats', 'v3'], // Version bump to force refresh
    queryFn: async () => {
      const response = await apiClient.get('/team-filters/stats')
      const data = response.data

      if (!data.success) {
        throw new Error(data.message || 'Failed to load team statistics')
      }

      return data.data
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false
  })
}

export const useTeamsWithCounts = () => {
  return useQuery({
    queryKey: ['teams-with-counts', 'v2'], // Version bump to force cache refresh for InfraDevopService fix
    queryFn: async () => {
      console.log('🔍 useTeamsWithCounts: Fetching teams with counts from API')
      const response = await apiClient.get('/team-filters/teams-with-counts')
      const data = response.data

      if (!data.success) {
        throw new Error(data.message || 'Failed to load teams with counts')
      }

      console.log('✅ useTeamsWithCounts: Received teams data:', data.data?.length, 'teams')

      // Log InfraDevopService specifically for debugging
      const infraDevopService = data.data?.find(team => team.name === 'InfraDevopService')
      if (infraDevopService) {
        console.log('🔍 useTeamsWithCounts: InfraDevopService count:', infraDevopService.count)
      }

      return data.data || []
    },
    staleTime: 5 * 60 * 1000, // Reduced to 5 minutes for more frequent updates
    refetchOnWindowFocus: false
  })
}

/**
 * Hook to invalidate team-related caches
 * Useful for forcing refresh when team data might be stale
 */
export const useInvalidateTeamCaches = () => {
  const queryClient = useQueryClient()

  return {
    invalidateTeamsWithCounts: () => {
      console.log('🔄 Invalidating teams-with-counts cache')
      queryClient.invalidateQueries({ queryKey: ['teams-with-counts'] })
    },
    invalidateTeamStats: () => {
      console.log('🔄 Invalidating team-stats cache')
      queryClient.invalidateQueries({ queryKey: ['team-stats'] })
    },
    invalidateAllTeamData: () => {
      console.log('🔄 Invalidating all team-related caches')
      queryClient.invalidateQueries({ queryKey: ['teams-with-counts'] })
      queryClient.invalidateQueries({ queryKey: ['team-stats'] })
      queryClient.invalidateQueries({ queryKey: ['teams'] })
      queryClient.invalidateQueries({ queryKey: ['team-enriched-initiatives'] })
    }
  }
}
